import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { History, RotateCcw, Archive, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useRole } from "../context/RoleContext";
import { toast } from "sonner";

// Cluster 14 — every Clear & Replace snapshots the prior FRS and its progress.
// Administrators can restore a snapshot; archived progress stays viewable.
export default function FrsSnapshotsDialog({ projectId, open, onOpenChange = () => {}, onRestored, inline = false }) {
  const [snaps, setSnaps] = useState([]);
  const [archives, setArchives] = useState([]);
  const [busy, setBusy] = useState("");
  const { role } = useRole();
  const isAdmin = String(role || "").toLowerCase().match(/^(admin|management)$/) !== null;

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get(`/projects/${projectId}/frs-snapshots`),
        api.get(`/projects/${projectId}/frs-archives`),
      ]);
      setSnaps(a.data || []);
      setArchives(b.data || []);
    } catch { setSnaps([]); setArchives([]); }
  };
  useEffect(() => { if ((open || inline) && projectId) load(); /* eslint-disable-next-line */ }, [open, inline, projectId]);

  const restore = async (id) => {
    if (!window.confirm("Restore this snapshot? The current FRS, its progress and pool cards are replaced with the snapshot.")) return;
    setBusy(id);
    try {
      await api.post(`/projects/${projectId}/frs-snapshots/${id}/restore`, null, {
        params: { role: role || "", actor: localStorage.getItem("fieldops-installer-name") || role || "" },
      });
      toast.success("Snapshot restored");
      onRestored?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Restore failed");
    } finally { setBusy(""); }
  };

  const body = (
        <div className="space-y-4 text-sm">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Snapshots</div>
            {snaps.length === 0 ? (
              <div className="text-xs text-gray-400" data-testid="frs-snapshots-empty">
                No snapshots yet — one is taken automatically on every Clear &amp; Replace.
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead className="text-gray-500 uppercase text-[10px]">
                  <tr>
                    <th className="text-left py-1">Taken</th><th className="text-left py-1">By</th>
                    <th className="text-left py-1">Replaced by</th><th className="text-right py-1">Rows</th>
                    <th className="text-right py-1">Progress</th><th className="py-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {snaps.map((s) => (
                    <tr key={s.id} className="border-t border-gray-100" data-testid={`frs-snapshot-${s.id}`}>
                      <td className="py-1 whitespace-nowrap">{new Date(s.created_at).toLocaleString()}</td>
                      <td className="py-1">{s.by || "—"}</td>
                      <td className="py-1 text-gray-600">{s.reason === "pre_restore" ? "pre-restore backup" : (s.replaced_by_file || "—")}</td>
                      <td className="py-1 text-right tabular-nums">{s.counts?.frs_items ?? 0}</td>
                      <td className="py-1 text-right tabular-nums">{s.counts?.completions ?? 0} compl · {s.counts?.pool_cards ?? 0} cards</td>
                      <td className="py-1 text-right">
                        <Button size="sm" variant="outline" disabled={!isAdmin || busy === s.id}
                                onClick={() => restore(s.id)} data-testid={`frs-snapshot-restore-${s.id}`}
                                title={isAdmin ? "Restore this snapshot" : "Administrators only"}>
                          {busy === s.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />}
                          Restore
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1 flex items-center gap-1">
              <Archive className="w-3.5 h-3.5" /> Archived progress
            </div>
            {archives.length === 0 ? (
              <div className="text-xs text-gray-400" data-testid="frs-archives-empty">Nothing archived.</div>
            ) : (
              archives.map((a) => (
                <div key={a.id} className="border border-gray-200 rounded p-2 mb-2" data-testid={`frs-archive-${a.id}`}>
                  <div className="text-xs text-gray-700">
                    {new Date(a.created_at).toLocaleString()} · {a.room_count} room(s) · {a.completion_count} completion(s)
                  </div>
                  <ul className="text-[11px] text-gray-600 mt-1 list-disc pl-5">
                    {(a.items || []).map((it, i) => (
                      <li key={i}>
                        {[it.building, it.floor && `Fl ${it.floor}`, it.room_number, it.room_type].filter(Boolean).join(" · ")}
                        {" — "}{it.scope_type} · {Math.round((it.percent_complete || 0) * 100)}% by {it.author || "—"}
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </div>
        </div>
  );
  if (inline) return (
    <div className="space-y-2" data-testid="frs-history-tab">
      <p className="text-xs text-muted-foreground">A snapshot is taken automatically before every FRS Clear &amp; Replace and before every restore. Administrators can restore one; archived progress stays viewable.</p>
      {body}
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="frs-snapshots-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-4 h-4 text-blue-700" /> FRS snapshots &amp; archived progress
          </DialogTitle>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}
